import React from 'react';
import { withTranslation } from 'react-i18next';
import {
  Paper, Grid, FormControl, InputLabel, Select, OutlinedInput, Button, MenuItem,
  CircularProgress, Tooltip, IconButton,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import * as strings from '../../helpers/strings';

const DownloadReportPortfolioSelect = ({
  t, stations, selectedStationName, selectedYear, selectedMonth, selectedDay,
  years, months, days, reportType,
  isLoadingYears, isLoadingMonths, isLoadingDays, isSubmitting,
  onStationChange, onYearChange, onMonthChange, onDayChange, onSubmit, onReset,
}) => {
  const getMonthName = yearMonth => {
    const parts = yearMonth.split('-');
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
    return date.toLocaleString('default', { month: 'long' });
  };
  const sortedStations = stations.slice().sort((a, b) => a.name.localeCompare(b.name));
  const supportsDaySelection = reportType === 'hourly' || reportType === 'half-hourly';
  const reportLabelKey = {
    monthly: 'monthly-report',
    'half-hourly': 'half-hourly-report',
    hourly: 'hourly-report',
  }[reportType] || 'monthly-report';
  const helpTextKey = {
    hourly: 'download-hourly-selection-help',
    'half-hourly': 'download-half-hourly-selection-help',
  }[reportType] || 'download-report-selection-help';

  return (
    <Paper className="downloadReportPortfolioFilters">
      <Grid container spacing={16}>
        <Grid item md={12} sm={12} xs={12}>
          <p className="downloadReportTypeLabel">{t(reportLabelKey)}</p>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <FormControl variant={strings.txtOutlined} fullWidth>
            <InputLabel htmlFor="portfolio-report-station">{t('station')}</InputLabel>
            <Select
              value={selectedStationName}
              onChange={event => onStationChange(event.target.value)}
              input={<OutlinedInput labelWidth={55} id="portfolio-report-station" />}
            >
              <MenuItem value="All">{t('status-all')}</MenuItem>
              {sortedStations.map(station => (
                <MenuItem key={station.id} value={station.name}>
                  {station.name} - {station.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <FormControl className="portfolioReportSelectControl" variant={strings.txtOutlined} fullWidth>
            <InputLabel htmlFor="portfolio-report-year">{t('year')}</InputLabel>
            <Select
              value={selectedYear}
              disabled={!selectedStationName || isLoadingYears}
              inputProps={{ 'aria-busy': isLoadingYears }}
              onChange={event => onYearChange(event.target.value)}
              input={<OutlinedInput labelWidth={45} id="portfolio-report-year" />}
            >
              {years.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
            </Select>
            {isLoadingYears && (
              <CircularProgress className="portfolioReportDropdownLoader" size={18} thickness={5} />
            )}
          </FormControl>
        </Grid>
        <Grid item md={2} sm={6} xs={12}>
          <FormControl className="portfolioReportSelectControl" variant={strings.txtOutlined} fullWidth>
            <InputLabel htmlFor="portfolio-report-month">{t('month')}</InputLabel>
            <Select
              value={selectedMonth}
              disabled={!selectedYear || isLoadingMonths}
              inputProps={{ 'aria-busy': isLoadingMonths }}
              onChange={event => onMonthChange(event.target.value)}
              input={<OutlinedInput labelWidth={55} id="portfolio-report-month" />}
            >
              {selectedStationName !== 'All' && (
                <MenuItem value="">{t('status-all')}</MenuItem>
              )}
              {months.map(month => (
                <MenuItem key={month} value={month}>{getMonthName(month)}</MenuItem>
              ))}
            </Select>
            {isLoadingMonths && (
              <CircularProgress className="portfolioReportDropdownLoader" size={18} thickness={5} />
            )}
          </FormControl>
        </Grid>
        {supportsDaySelection && (
          <Grid item md={2} sm={6} xs={12}>
            <FormControl className="portfolioReportSelectControl" variant={strings.txtOutlined} fullWidth>
              <InputLabel htmlFor="download-report-day">{t('day')}</InputLabel>
              <Select
                value={selectedDay}
                disabled={!selectedMonth || isLoadingDays}
                inputProps={{ 'aria-busy': isLoadingDays }}
                onChange={event => onDayChange(event.target.value)}
                input={<OutlinedInput labelWidth={40} id="download-report-day" />}
              >
                <MenuItem value="">{t('status-all')}</MenuItem>
                {days.map(day => <MenuItem key={day} value={day}>{day}</MenuItem>)}
              </Select>
              {isLoadingDays && (
                <CircularProgress className="portfolioReportDropdownLoader" size={18} thickness={5} />
              )}
            </FormControl>
          </Grid>
        )}
        <Grid item md={supportsDaySelection ? 4 : 6} sm={6} xs={12} />
      </Grid>
      <div className="ReportButtonDiv downloadReportPortfolioButtons">
        <Button
          variant={strings.btnRaised}
          color={strings.colorPrimary}
          disabled={
            !selectedStationName ||
            !selectedYear ||
            (selectedStationName === 'All' && !selectedMonth) ||
            isSubmitting
          }
          onClick={onSubmit}
        >
          {t('submit')}
        </Button>
        <Button
          variant={strings.btnRaised}
          color={strings.colorPrimary}
          disabled={!selectedStationName && !selectedYear && !selectedMonth}
          onClick={onReset}
        >
          {t('reset')}
        </Button>
      </div>
      <Tooltip
        title={t(helpTextKey)}
        placement="top-end"
        enterDelay={300}
      >
        <IconButton
          className="downloadReportSelectionHelp"
          aria-label={t(helpTextKey)}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export default withTranslation()(DownloadReportPortfolioSelect);
